import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { Badge } from '../ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Alert, AlertDescription } from '../ui/alert';
import { Progress } from '../ui/progress';
import { 
  Shield, 
  ShieldCheck, 
  ShieldAlert, 
  ShieldX,
  Key, 
  Smartphone, 
  Eye, 
  EyeOff,
  Lock,
  Unlock,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Clock,
  Globe,
  Download,
  Upload,
  Trash2
} from 'lucide-react';

interface SecurityEvent {
  id: string;
  type: 'login' | 'transaction' | 'setting_change' | 'failed_attempt';
  description: string;
  timestamp: string;
  location: string;
  ip: string;
  status: 'success' | 'failed' | 'warning';
}

export function SecurityDashboard() {
  const [twoFactorEnabled, setTwoFactorEnabled] = useState(true);
  const [biometricEnabled, setBiometricEnabled] = useState(false);
  const [showBackupCodes, setShowBackupCodes] = useState(false);
  const [transactionLimits, setTransactionLimits] = useState({
    daily: '5000',
    monthly: '50000'
  });

  const securityEvents: SecurityEvent[] = [
    {
      id: '1',
      type: 'login',
      description: 'Successful login from Chrome on MacOS',
      timestamp: '2024-01-15 14:30:22',
      location: 'New York, US',
      ip: '192.168.1.100',
      status: 'success'
    },
    {
      id: '2',
      type: 'transaction',
      description: 'Large transaction sent (2.5 ETH)',
      timestamp: '2024-01-15 12:45:10',
      location: 'New York, US',
      ip: '192.168.1.100',
      status: 'success'
    },
    {
      id: '3',
      type: 'failed_attempt',
      description: 'Failed login attempt',
      timestamp: '2024-01-14 18:20:15',
      location: 'Unknown',
      ip: '45.76.123.45',
      status: 'failed'
    },
    {
      id: '4',
      type: 'setting_change',
      description: '2FA settings updated',
      timestamp: '2024-01-14 09:15:30',
      location: 'New York, US',
      ip: '192.168.1.100',
      status: 'warning'
    }
  ];

  const getEventIcon = (type: string, status: string) => {
    if (status === 'failed') return <XCircle className="h-4 w-4 text-red-500" />;
    if (status === 'warning') return <AlertTriangle className="h-4 w-4 text-yellow-500" />;
    
    switch (type) {
      case 'login': return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'transaction': return <CheckCircle className="h-4 w-4 text-blue-500" />;
      case 'setting_change': return <CheckCircle className="h-4 w-4 text-purple-500" />;
      default: return <CheckCircle className="h-4 w-4 text-gray-500" />;
    }
  };

  const securityScore = 85;
  const getScoreColor = (score: number) => {
    if (score >= 80) return 'text-green-600';
    if (score >= 60) return 'text-yellow-600';
    return 'text-red-600';
  };

  const backupCodes = [
    '1234-5678-9012',
    '3456-7890-1234',
    '5678-9012-3456',
    '7890-1234-5678',
    '9012-3456-7890',
    '1234-5678-9012'
  ];

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold">Security Center</h1>
          <p className="text-slate-600 dark:text-slate-400 mt-2">
            Manage your account security and monitor activity
          </p>
        </div>
        <Badge variant="outline" className="gap-2">
          <Shield className="h-4 w-4" />
          Security Score: <span className={getScoreColor(securityScore)}>{securityScore}/100</span>
        </Badge>
      </div>

      {/* Security Overview */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-600 dark:text-slate-400">Security Score</p>
                <p className={`text-2xl font-bold ${getScoreColor(securityScore)}`}>{securityScore}/100</p>
              </div>
              <div className="w-12 h-12 bg-green-50 dark:bg-green-900/20 rounded-xl flex items-center justify-center">
                <ShieldCheck className="h-6 w-6 text-green-600" />
              </div>
            </div>
            <Progress value={securityScore} className="mt-3" />
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-600 dark:text-slate-400">2FA Status</p>
                <p className="text-lg font-bold text-green-600">Enabled</p>
              </div>
              <div className="w-12 h-12 bg-blue-50 dark:bg-blue-900/20 rounded-xl flex items-center justify-center">
                <Smartphone className="h-6 w-6 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-600 dark:text-slate-400">Active Sessions</p>
                <p className="text-2xl font-bold">3</p>
              </div>
              <div className="w-12 h-12 bg-purple-50 dark:bg-purple-900/20 rounded-xl flex items-center justify-center">
                <Globe className="h-6 w-6 text-purple-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-600 dark:text-slate-400">Failed Attempts</p>
                <p className="text-2xl font-bold text-red-600">2</p>
              </div>
              <div className="w-12 h-12 bg-red-50 dark:bg-red-900/20 rounded-xl flex items-center justify-center">
                <ShieldAlert className="h-6 w-6 text-red-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="authentication" className="space-y-6">
        <TabsList>
          <TabsTrigger value="authentication">Authentication</TabsTrigger>
          <TabsTrigger value="limits">Transaction Limits</TabsTrigger>
          <TabsTrigger value="sessions">Active Sessions</TabsTrigger>
          <TabsTrigger value="activity">Security Activity</TabsTrigger>
        </TabsList>

        <TabsContent value="authentication" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Smartphone className="h-5 w-5" />
                  Two-Factor Authentication
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium">SMS Authentication</p>
                    <p className="text-sm text-slate-600 dark:text-slate-400">
                      Receive codes via SMS to your phone
                    </p>
                  </div>
                  <Switch 
                    checked={twoFactorEnabled} 
                    onCheckedChange={setTwoFactorEnabled}
                  />
                </div>
                
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium">Authenticator App</p>
                    <p className="text-sm text-slate-600 dark:text-slate-400">
                      Use Google Authenticator or similar apps
                    </p>
                  </div>
                  <Switch checked={true} disabled />
                </div>

                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium">Biometric Authentication</p>
                    <p className="text-sm text-slate-600 dark:text-slate-400">
                      Use fingerprint or face recognition
                    </p>
                  </div>
                  <Switch 
                    checked={biometricEnabled} 
                    onCheckedChange={setBiometricEnabled}
                  />
                </div>

                <div className="pt-4 border-t">
                  <Button 
                    variant="outline" 
                    className="w-full gap-2"
                    onClick={() => setShowBackupCodes(!showBackupCodes)}
                  >
                    {showBackupCodes ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    {showBackupCodes ? 'Hide' : 'Show'} Backup Codes
                  </Button>
                  
                  {showBackupCodes && (
                    <div className="mt-4 p-4 bg-slate-50 dark:bg-slate-800 rounded-lg">
                      <p className="text-sm font-medium mb-2">Backup Recovery Codes:</p>
                      <div className="grid grid-cols-2 gap-2 text-sm font-mono">
                        {backupCodes.map((code, index) => (
                          <div key={index} className="p-2 bg-white dark:bg-slate-700 rounded border">
                            {code}
                          </div>
                        ))}
                      </div>
                      <div className="flex gap-2 mt-4">
                        <Button size="sm" variant="outline" className="gap-1">
                          <Download className="h-3 w-3" />
                          Download
                        </Button>
                        <Button size="sm" variant="outline" className="gap-1">
                          <Upload className="h-3 w-3" />
                          Regenerate
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Key className="h-5 w-5" />
                  Password Security
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label>Current Password</Label>
                  <Input type="password" placeholder="Enter current password" />
                </div>
                
                <div className="space-y-2">
                  <Label>New Password</Label>
                  <Input type="password" placeholder="Enter new password" />
                </div>
                
                <div className="space-y-2">
                  <Label>Confirm New Password</Label>
                  <Input type="password" placeholder="Confirm new password" />
                </div>

                <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                  <p className="text-sm text-blue-800 dark:text-blue-200">
                    Password strength: <strong>Strong</strong>
                  </p>
                  <Progress value={85} className="mt-2" />
                </div>

                <Button className="w-full">
                  Update Password
                </Button>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="limits" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Lock className="h-5 w-5" />
                Transaction Limits
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <Alert>
                <AlertTriangle className="h-4 w-4" />
                <AlertDescription>
                  Setting transaction limits helps protect your account from unauthorized large transactions.
                </AlertDescription>
              </Alert>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label>Daily Transaction Limit (USD)</Label>
                  <Input 
                    type="number" 
                    value={transactionLimits.daily}
                    onChange={(e) => setTransactionLimits(prev => ({ ...prev, daily: e.target.value }))}
                  />
                  <p className="text-sm text-slate-600 dark:text-slate-400">
                    Current usage: $1,250 / $5,000 (25%)
                  </p>
                  <Progress value={25} />
                </div>

                <div className="space-y-2">
                  <Label>Monthly Transaction Limit (USD)</Label>
                  <Input 
                    type="number" 
                    value={transactionLimits.monthly}
                    onChange={(e) => setTransactionLimits(prev => ({ ...prev, monthly: e.target.value }))}
                  />
                  <p className="text-sm text-slate-600 dark:text-slate-400">
                    Current usage: $18,900 / $50,000 (38%)
                  </p>
                  <Progress value={38} />
                </div>
              </div>

              <div className="flex gap-2">
                <Button>Save Limits</Button>
                <Button variant="outline">Reset to Default</Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="sessions" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Globe className="h-5 w-5" />
                Active Sessions
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="flex items-center justify-between p-4 border rounded-lg">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-green-50 dark:bg-green-900/20 rounded-lg flex items-center justify-center">
                      <CheckCircle className="h-5 w-5 text-green-600" />
                    </div>
                    <div>
                      <p className="font-medium">Current Session</p>
                      <p className="text-sm text-slate-600 dark:text-slate-400">
                        Chrome on MacOS • New York, US
                      </p>
                      <p className="text-xs text-slate-500">Last active: Now</p>
                    </div>
                  </div>
                  <Badge variant="secondary">Current</Badge>
                </div>

                <div className="flex items-center justify-between p-4 border rounded-lg">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-blue-50 dark:bg-blue-900/20 rounded-lg flex items-center justify-center">
                      <Smartphone className="h-5 w-5 text-blue-600" />
                    </div>
                    <div>
                      <p className="font-medium">Mobile App</p>
                      <p className="text-sm text-slate-600 dark:text-slate-400">
                        iPhone • New York, US
                      </p>
                      <p className="text-xs text-slate-500">Last active: 2 hours ago</p>
                    </div>
                  </div>
                  <Button variant="outline" size="sm">
                    <Trash2 className="h-3 w-3 mr-1" />
                    Revoke
                  </Button>
                </div>

                <div className="flex items-center justify-between p-4 border rounded-lg">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-purple-50 dark:bg-purple-900/20 rounded-lg flex items-center justify-center">
                      <Globe className="h-5 w-5 text-purple-600" />
                    </div>
                    <div>
                      <p className="font-medium">Firefox on Windows</p>
                      <p className="text-sm text-slate-600 dark:text-slate-400">
                        Windows 11 • Los Angeles, US
                      </p>
                      <p className="text-xs text-slate-500">Last active: 1 day ago</p>
                    </div>
                  </div>
                  <Button variant="outline" size="sm">
                    <Trash2 className="h-3 w-3 mr-1" />
                    Revoke
                  </Button>
                </div>
              </div>

              <div className="mt-6 pt-4 border-t">
                <Button variant="destructive" className="gap-2">
                  <ShieldX className="h-4 w-4" />
                  Revoke All Other Sessions
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="activity" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Clock className="h-5 w-5" />
                Recent Security Activity
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {securityEvents.map((event) => (
                  <div key={event.id} className="flex items-start gap-3 p-4 border rounded-lg">
                    {getEventIcon(event.type, event.status)}
                    <div className="flex-1">
                      <p className="font-medium">{event.description}</p>
                      <div className="flex items-center gap-4 text-sm text-slate-600 dark:text-slate-400 mt-1">
                        <span>{event.timestamp}</span>
                        <span>•</span>
                        <span>{event.location}</span>
                        <span>•</span>
                        <span className="font-mono">{event.ip}</span>
                      </div>
                    </div>
                    <Badge 
                      variant={event.status === 'success' ? 'secondary' : 
                               event.status === 'failed' ? 'destructive' : 'outline'}
                      className="capitalize"
                    >
                      {event.status}
                    </Badge>
                  </div>
                ))}
              </div>

              <div className="mt-6 text-center">
                <Button variant="outline">Load More Activity</Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}